import { useId, useState } from 'react'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { formatDateTime } from '@/lib/formatAgent'
import { cn } from '@/lib/utils'

/** Shared “Collected …” label for RMM / sync timestamps. */
export function formatCollectedLabel(iso, { prefix = 'Collected' } = {}) {
  if (!iso) return null
  return `${prefix} ${formatDateTime(iso)}`
}

const collectedEmphasisClass =
  'inline-block rounded-md bg-slate-200/90 px-2 py-0.5 font-semibold tabular-nums text-foreground'

/** Emphasised collected timestamp for backup/NAS card summaries. */
export function CollectedAtEmphasis({
  collectedAt,
  prefix = 'Collected',
  neverLabel = null,
  className,
}) {
  if (!collectedAt) {
    if (!neverLabel) return null
    return (
      <span className={cn(collectedEmphasisClass, className)}>
        {neverLabel}
      </span>
    )
  }
  const label = prefix ? `${prefix} ${formatDateTime(collectedAt)}` : formatDateTime(collectedAt)
  return (
    <span className={cn(collectedEmphasisClass, className)}>
      {label}
    </span>
  )
}

export function CollectedAtLine({
  collectedAt,
  prefix = 'Collected',
  className,
  emphasise = false,
}) {
  if (!collectedAt) return null
  if (emphasise) {
    return (
      <p className={cn('text-xs', className)}>
        <CollectedAtEmphasis collectedAt={collectedAt} prefix={prefix} />
      </p>
    )
  }
  const text = formatCollectedLabel(collectedAt, { prefix })
  return (
    <p className={cn('text-xs text-muted-foreground', className)}>{text}</p>
  )
}

/** Join plain strings and React nodes with · for CollapsePanel summaries. */
export function joinCollapsedSummary(parts) {
  const filtered = parts.filter(Boolean)
  if (filtered.length === 0) return undefined
  return filtered.flatMap((part, index) => (index === 0 ? [part] : [' · ', part]))
}

/**
 * Expandable snapshot/info card with last-collected visible when collapsed and open.
 * Appends collectedAt to any custom collapsedSummary (does not replace it).
 */
export function SnapshotCard({
  title,
  collectedAt,
  children,
  pill,
  pillVariant,
  collapsedSummary,
  collectedPrefix = 'Collected',
  idPrefix = 'snapshot-card',
  bodyClassName = 'space-y-2',
  className,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  showCollectedWhenOpen = true,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const id = useId()
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const handleOpenChange = isControlled ? onOpenChange : setUncontrolledOpen

  const collectedLine = formatCollectedLabel(collectedAt, { prefix: collectedPrefix })
  const summaryBits = []
  if (collapsedSummary) summaryBits.push(collapsedSummary)
  if (collectedLine && !String(collapsedSummary || '').includes(collectedLine)) {
    summaryBits.push(collectedLine)
  }

  return (
    <CollapsePanel
      id={`${idPrefix}-${id}`}
      title={title}
      collapsedSummary={summaryBits.length ? summaryBits.join(' · ') : undefined}
      open={open}
      onOpenChange={handleOpenChange}
      pill={pill}
      pillVariant={pillVariant}
      className={className}
      bodyClassName={bodyClassName}
    >
      {showCollectedWhenOpen ? (
        <CollectedAtLine
          collectedAt={collectedAt}
          prefix={collectedPrefix}
          className="mb-1"
        />
      ) : null}
      {children}
    </CollapsePanel>
  )
}
